import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import axios from 'axios';

interface Props {
  initialHTML?: string;
  onSave?: (html: string, json: unknown) => void;
  placeholder?: string;
  className?: string;
}

const BlogEditor: React.FC<Props> = ({ 
  initialHTML = '<p></p>', 
  onSave, 
  placeholder = 'Start writing your blog post...',
  className = ''
}) => {
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to ensure images are properly embedded
  const preprocessContent = useCallback((html: string) => {
    // If html contains image URLs (not embedded img tags), convert them to img tags
    const urlRegex = /(http:\/\/127\.0\.0\.1:8000\/storage\/[^\s]+\.(?:png|jpg|jpeg|gif|webp))/gi;
    const processedHtml = html.replace(urlRegex, '<img src="$1" alt="Blog content" class="editor-image" style="max-width: 100%; height: auto; border-radius: 8px; margin: 16px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.1); display: block;" />');
    return processedHtml;
  }, []);

  const debouncedSave = useCallback((html: string, json: unknown) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      onSave?.(html, json);
    }, 1000); // Save after 1 second of no typing
  }, [onSave]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: 'editor-image',
          style: 'max-width: 100%; height: auto; border-radius: 8px; margin: 16px 0; box-shadow: 0 4px 12px rgba(0,0,0,0.1);',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 hover:text-blue-800 underline',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: preprocessContent(initialHTML),
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    onUpdate: ({ editor }) => {
      // Debounced auto-save to prevent interruption during typing
      debouncedSave(editor.getHTML(), editor.getJSON());
    },
    editorProps: {
      attributes: {
        placeholder: placeholder,
        class: 'prose prose-lg max-w-none focus:outline-none min-h-[300px] px-4 py-3',
      },
    },
  });

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const uploadImage = useCallback(async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File too large. Please select an image smaller than 5MB');
        return;
      }

      try {
        setIsUploadingImage(true);
        
        const formData = new FormData();
        formData.append('image', file);
        
        const token = localStorage.getItem('token');
        if (!token) {
          alert('Please log in to upload images');
          return;
        }

        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
        
        const response = await axios.post(`${API_BASE_URL}/admin/blog/upload-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.data.success && response.data.data.url) {
          editor?.chain().focus().setImage({
            src: response.data.data.url,
            alt: 'Blog content image',
            title: 'Blog content image'
          }).run();
          
          // Force re-render to ensure image appears
          setTimeout(() => {
            editor?.commands.focus();
          }, 100);
        } else {
          throw new Error(response.data.message || 'Upload failed');
        }
      } catch (error: unknown) {
        console.error('Image upload error:', error);
        let errorMessage = 'Failed to upload image';
        
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { data?: { message?: string } } };
          errorMessage = axiosError.response?.data?.message || errorMessage;
        }
        
        alert(errorMessage);
      } finally {
        setIsUploadingImage(false);
      }
    };
  }, [editor]);

  const insertYouTubeVideo = useCallback(() => {
    const url = prompt('Enter YouTube video URL:');
    if (url) {
      const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)?.[1];
      if (videoId) {
        const embedHtml = `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen></iframe>`;
        editor?.chain().focus().insertContent(embedHtml).run();
      } else {
        alert('Please enter a valid YouTube URL');
      }
    }
  }, [editor]);

  if (!editor) return null;

  const ToolbarButton: React.FC<{
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    title: string;
  }> = ({ onClick, isActive, disabled, children, title }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-2 rounded-md transition-colors duration-200 ${
        isActive 
          ? 'bg-blue-600 text-white' 
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {children}
    </button>
  );

  return (
    <div className={`border border-gray-300 rounded-lg bg-white ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        {/* Text Formatting */}
        <div className="flex items-center gap-1">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            title="Bold"
          >
            <i className="fa-solid fa-bold text-sm"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            title="Italic"
          >
            <i className="fa-solid fa-italic text-sm"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
            title="Strikethrough"
          >
            <i className="fa-solid fa-strikethrough text-sm"></i>
          </ToolbarButton>
        </div>

        {/* Headings */}
        <div className="flex items-center gap-1 ml-2">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            isActive={editor.isActive('heading', { level: 1 })}
            title="Heading 1"
          >
            <i className="fa-solid fa-heading text-sm"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive('heading', { level: 2 })}
            title="Heading 2"
          >
            <i className="fa-solid fa-heading text-xs"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            isActive={editor.isActive('heading', { level: 3 })}
            title="Heading 3"
          >
            <i className="fa-solid fa-heading text-xs" style={{ fontSize: '10px' }}></i>
          </ToolbarButton>
        </div>

        {/* Lists */}
        <div className="flex items-center gap-1 ml-2">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title="Bullet List"
          >
            <i className="fa-solid fa-list-ul text-sm"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title="Numbered List"
          >
            <i className="fa-solid fa-list-ol text-sm"></i>
          </ToolbarButton>
        </div>

        {/* Block Elements */}
        <div className="flex items-center gap-1 ml-2">
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            title="Quote"
          >
            <i className="fa-solid fa-quote-right text-sm"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title="Horizontal Rule"
          >
            <i className="fa-solid fa-minus text-sm"></i>
          </ToolbarButton>
        </div>

        {/* Links */}
        <div className="flex items-center gap-1 ml-2">
          <ToolbarButton
            onClick={() => {
              const url = window.prompt('Enter URL:');
              if (url) {
                editor.chain().focus().setLink({ href: url }).run();
              }
            }}
            isActive={editor.isActive('link')}
            title="Insert Link"
          >
            <i className="fa-solid fa-link text-sm"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().unsetLink().run()}
            isActive={editor.isActive('link')}
            title="Remove Link"
          >
            <i className="fa-solid fa-unlink text-sm"></i>
          </ToolbarButton>
        </div>

        {/* Media */}
        <div className="flex items-center gap-1 ml-2">
          <ToolbarButton
            onClick={uploadImage}
            disabled={isUploadingImage}
            title="Insert Image"
          >
            {isUploadingImage ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
            ) : (
              <i className="fa-solid fa-image text-sm"></i>
            )}
          </ToolbarButton>
          <ToolbarButton
            onClick={insertYouTubeVideo}
            title="Insert YouTube Video"
          >
            <i className="fa-brands fa-youtube text-sm"></i>
          </ToolbarButton>
        </div>

        {/* Text Alignment */}
        <div className="flex items-center gap-1 ml-2">
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            isActive={editor.isActive({ textAlign: 'left' })}
            title="Align Left"
          >
            <i className="fa-solid fa-align-left text-sm"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            isActive={editor.isActive({ textAlign: 'center' })}
            title="Align Center"
          >
            <i className="fa-solid fa-align-center text-sm"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            isActive={editor.isActive({ textAlign: 'right' })}
            title="Align Right"
          >
            <i className="fa-solid fa-align-right text-sm"></i>
          </ToolbarButton>
        </div>

        {/* Undo/Redo */}
        <div className="flex items-center gap-1 ml-2">
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
          >
            <i className="fa-solid fa-undo text-sm"></i>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
          >
            <i className="fa-solid fa-redo text-sm"></i>
          </ToolbarButton>
        </div>
      </div>

      {/* Editor Content */}
      <div className="p-6 min-h-[400px] bg-white">
        <style>
          {`
            .ProseMirror {
              outline: none;
              min-height: 300px;
            }
            
            .ProseMirror p {
              margin-bottom: 1rem;
              line-height: 1.75;
            }
            
            .ProseMirror h1 {
              font-size: 2.25rem;
              font-weight: 700;
              margin-bottom: 1rem;
              line-height: 1.2;
            }
            
            .ProseMirror h2 {
              font-size: 1.875rem;
              font-weight: 700;
              margin-bottom: 0.875rem;
              line-height: 1.3;
            }
            
            .ProseMirror h3 {
              font-size: 1.5rem;
              font-weight: 600;
              margin-bottom: 0.75rem;
              line-height: 1.4;
            }
            
            .ProseMirror h4 {
              font-size: 1.25rem;
              font-weight: 600;
              margin-bottom: 0.625rem;
              line-height: 1.4;
            }
            
            .ProseMirror h5 {
              font-size: 1.125rem;
              font-weight: 600;
              margin-bottom: 0.5rem;
              line-height: 1.5;
            }
            
            .ProseMirror h6 {
              font-size: 1rem;
              font-weight: 600;
              margin-bottom: 0.5rem;
              line-height: 1.5;
            }
            
            .ProseMirror ul,
            .ProseMirror ol {
              padding-left: 1.5rem;
              margin-bottom: 1rem;
            }
            
            .ProseMirror li {
              margin-bottom: 0.5rem;
            }
            
            .ProseMirror blockquote {
              border-left: 4px solid #1E3A8A;
              padding-left: 1rem;
              margin: 1.5rem 0;
              font-style: italic;
              color: #4B5563;
            }
            
            .ProseMirror code {
              background-color: #F3F4F6;
              padding: 0.125rem 0.25rem;
              border-radius: 0.25rem;
              font-family: monospace;
              font-size: 0.875em;
            }
            
            .ProseMirror pre {
              background-color: #1F2937;
              color: #F9FAFB;
              padding: 1rem;
              border-radius: 0.5rem;
              overflow-x: auto;
              margin: 1rem 0;
            }
            
            .ProseMirror pre code {
              background-color: transparent;
              padding: 0;
              color: inherit;
            }
            
            .ProseMirror img {
              max-width: 100%;
              height: auto;
              border-radius: 0.5rem;
              margin: 1rem 0;
            }
            
            .ProseMirror a {
              color: #2563EB;
              text-decoration: underline;
            }
            
            .ProseMirror a:hover {
              color: #1E40AF;
            }
            
            .ProseMirror hr {
              border: none;
              border-top: 2px solid #E5E7EB;
              margin: 2rem 0;
            }
            
            .ProseMirror iframe {
              max-width: 100%;
              margin: 1.5rem 0;
              border-radius: 0.5rem;
            }
            
            /* Placeholder styling */
            .ProseMirror p.is-editor-empty:first-child::before {
              content: attr(data-placeholder);
              float: left;
              color: #9CA3AF;
              pointer-events: none;
              height: 0;
            }
          `}
        </style>
        <EditorContent 
          editor={editor}
          className="prose prose-lg max-w-none focus:outline-none"
        />
      </div>
    </div>
  );
};

export default BlogEditor;
